import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { z } from 'zod';

import { DataTable, type Column } from '@/components/data/DataTable';
import { DangerButton } from '@/components/danger/DangerButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { CopyableId } from '@/components/primitives/CopyableId';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import {
  ENTITY_TYPES,
  useAuditDetail,
  useAuditList,
  type AuditListRow,
  type AuditOutcome,
} from '@/features/audit/audit';
import { formatAge } from '@/features/dashboard/dashboard';
import { TierDot } from '@/routes/index';

const searchSchema = z.object({
  q: z.string().optional(),
  actorId: z.string().optional(),
  entityType: z.string().optional(),
  dangerTier: z.coerce.number().optional(),
  outcome: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().default(1),
  /** Selected row, expanded inline rather than on its own route. */
  row: z.string().optional(),
});

export const Route = createFileRoute('/audit')({
  validateSearch: searchSchema,
  component: AuditScreen,
});

const OUTCOME_TONE: Record<AuditOutcome, 'good' | 'bad' | 'warn'> = {
  succeeded: 'good',
  failed: 'bad',
  // Styled as badly as a failure on purpose: "unknown" means the action may or may
  // not have applied, and retrying a Tier 3 override that already landed is its own
  // incident. It must not read as a softer version of failed.
  unknown: 'bad',
  pending: 'warn',
};

const OUTCOME_LABEL: Record<AuditOutcome, string> = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  unknown: 'Unknown',
  pending: 'In flight',
};

function AuditScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useAuditList({
    q: search.q,
    actorId: search.actorId,
    entityType: search.entityType,
    dangerTier: search.dangerTier,
    outcome: search.outcome,
    from: search.from,
    to: search.to,
    page: search.page,
  });

  function setSearch(next: Record<string, unknown>) {
    void navigate({
      search: (previous) => ({ ...previous, ...next, page: (next.page as number) ?? 1 }),
      replace: true,
    });
  }

  const columns: Column<AuditListRow>[] = [
    {
      id: 'when',
      header: 'When',
      width: '128px',
      mobile: 'secondary',
      cell: (row) => (
        <span className="mesh-mono text-tx3 text-[11.5px]" title={row.createdAt ?? undefined}>
          {row.createdAt ? formatAge(row.createdAt) : '—'} ago
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      width: 'minmax(190px, 1.3fr)',
      mobile: 'primary',
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <TierDot tier={row.dangerTier} />
          <span className="mesh-mono text-tx truncate text-[12.5px]">{row.action}</span>
        </div>
      ),
    },
    {
      id: 'entity',
      header: 'Target',
      width: 'minmax(0, 1.2fr)',
      mobile: 'secondary',
      cell: (row) => (
        <span className="text-tx2 truncate text-[12.5px]">
          {row.entityLabel ?? row.entityId}
          <span className="text-tx3 ml-1.5 text-[11px]">{row.entityType}</span>
        </span>
      ),
    },
    {
      id: 'actor',
      header: 'Operator',
      width: 'minmax(0, 1fr)',
      mobile: 'detail',
      cell: (row) => (
        <span className="text-tx2 truncate text-[12.5px]" title={row.actor.email}>
          {row.actor.name}
        </span>
      ),
    },
    {
      id: 'outcome',
      header: 'Outcome',
      width: '132px',
      mobile: 'secondary',
      cell: (row) => (
        <StateChip
          state={row.outcome}
          size="sm"
          tone={OUTCOME_TONE[row.outcome]}
          label={OUTCOME_LABEL[row.outcome]}
        />
      ),
    },
    {
      id: 'via',
      header: 'Via',
      width: '92px',
      mobile: 'detail',
      cell: (row) => <span className="text-tx3 text-[11.5px]">{row.executedVia}</span>,
    },
  ];

  const activeFilters = [
    search.actorId,
    search.entityType,
    search.dangerTier,
    search.outcome,
    search.from,
    search.to,
  ].filter(Boolean).length;

  return (
    <RequireCapability capability="audit.view">
      <div className="flex flex-col gap-3.5">
        <PageHeader
          eyebrow="Governance"
          title={data ? `${data.total.toLocaleString()} recorded actions` : 'Audit'}
          meta="Append only · corrections are new entries, never edits"
          actions={
            <>
              <LiveDataIndicator
                updatedAt={dataUpdatedAt || undefined}
                isFetching={isFetching}
                onRefresh={() => void refetch()}
              />
              <DangerButton tier={1}>Export CSV</DangerButton>
            </>
          }
        />

        <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                aria-hidden
                className="text-tx3 pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <input
                type="search"
                value={search.q ?? ''}
                onChange={(event) => setSearch({ q: event.target.value })}
                placeholder="Action, target, reason or operator email…"
                aria-label="Search the audit log"
                className="border-bd bg-bg-alt text-tx placeholder:text-tx3 focus-visible:border-bd-s h-11 w-full rounded-lg border pr-2.5 pl-8 text-[13px] outline-none"
              />
            </div>
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() =>
                  setSearch({
                    actorId: undefined,
                    entityType: undefined,
                    dangerTier: undefined,
                    outcome: undefined,
                    from: undefined,
                    to: undefined,
                  })
                }
                className="border-bd text-tx2 hover:bg-elev hover:text-tx flex h-11 flex-none items-center gap-1.5 rounded-lg border px-2.5 text-xs"
              >
                <X size={12} aria-hidden />
                Clear {activeFilters}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <FilterSelect
              label="Operator"
              value={search.actorId ?? ''}
              onChange={(value) => setSearch({ actorId: value || undefined })}
              options={(data?.actors ?? []).map((actor) => ({
                value: actor.id,
                label: actor.name,
              }))}
            />
            <FilterSelect
              label="Tier"
              value={search.dangerTier ? String(search.dangerTier) : ''}
              onChange={(value) => setSearch({ dangerTier: value ? Number(value) : undefined })}
              options={[
                { value: '3', label: 'Tier 3 — override' },
                { value: '2', label: 'Tier 2 — write' },
                { value: '1', label: 'Tier 1 — safe' },
              ]}
            />
            <FilterSelect
              label="Outcome"
              value={search.outcome ?? ''}
              onChange={(value) => setSearch({ outcome: value || undefined })}
              options={[
                { value: 'unknown', label: 'Outcome unknown' },
                { value: 'failed', label: 'Failed' },
                { value: 'succeeded', label: 'Succeeded' },
                { value: 'pending', label: 'In flight' },
              ]}
            />
            <FilterSelect
              label="Entity"
              value={search.entityType ?? ''}
              onChange={(value) => setSearch({ entityType: value || undefined })}
              options={ENTITY_TYPES.map((type) => ({ value: type, label: type }))}
            />
            <label className="flex items-center gap-1.5">
              <span className="sr-only">From date</span>
              <input
                type="date"
                value={search.from ?? ''}
                onChange={(event) => setSearch({ from: event.target.value || undefined })}
                aria-label="From date"
                className="border-bd bg-bg-alt text-tx2 h-9 rounded-lg border px-2 text-[12.5px] outline-none"
              />
            </label>
          </div>
        </div>

        {isError ? (
          <ErrorState error={error} subject="the audit log" onRetry={() => void refetch()} />
        ) : (
          <>
            <DataTable
              label="Audit log"
              columns={columns}
              rows={data?.rows ?? []}
              rowKey={(row) => row.id}
              isLoading={isPending}
              emptyState={
                <EmptyState
                  title="No matching actions"
                  description="Every Tier 2 and Tier 3 action is recorded here as it happens. An empty log means nothing has been done yet, not that anything is missing."
                />
              }
            />

            {/*
              Selection expands inline rather than navigating. An investigation is a
              scan-and-drill loop, and losing the filtered list on every drill turns
              it into re-filtering.
            */}
            <RowDetail
              auditId={search.row ?? null}
              rows={data?.rows ?? []}
              onSelect={(id) => setSearch({ row: id ?? undefined, page: search.page })}
            />

            {data && data.total > data.pageSize && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(page) => setSearch({ page })}
              />
            )}
          </>
        )}
      </div>
    </RequireCapability>
  );
}

function RowDetail({
  auditId,
  rows,
  onSelect,
}: {
  auditId: string | null;
  rows: AuditListRow[];
  onSelect: (id: string | null) => void;
}) {
  const { data, isPending, isError, error } = useAuditDetail(auditId);

  if (!auditId) {
    return rows.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        <span className="text-tx3 text-[11.5px]">Inspect a row:</span>
        {rows.slice(0, 8).map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => onSelect(row.id)}
            className="border-bd text-tx3 hover:bg-elev hover:text-tx mesh-mono rounded border px-1.5 py-0.5 text-[11px]"
          >
            {row.action}
          </button>
        ))}
      </div>
    ) : null;
  }

  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h2
          className="font-head text-tx2 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          Action detail
        </h2>
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="text-tx3 hover:text-tx text-[11.5px]"
        >
          Close
        </button>
      </div>

      {isError ? (
        <ErrorState error={error} subject="this audit record" />
      ) : isPending ? (
        <Skeleton height={140} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <TierDot tier={data.dangerTier} />
            <span className="mesh-mono text-tx text-[13px]">{data.action}</span>
            <StateChip
              state={data.outcome}
              size="sm"
              tone={OUTCOME_TONE[data.outcome]}
              label={OUTCOME_LABEL[data.outcome]}
            />
            <CopyableId value={data.entityId} label="Entity ID" truncate />
          </div>

          {/* Mandatory for Tier 3, and the first thing an investigation reads. */}
          {data.reason && (
            <div>
              <FieldLabel>Reason given</FieldLabel>
              <p className="text-tx mt-0.5 text-[13px] italic">"{data.reason}"</p>
            </div>
          )}

          {data.outcome === 'unknown' && (
            <p
              role="alert"
              className="rounded-lg border px-3 py-2 text-[12.5px]"
              style={{
                borderColor: 'rgba(var(--err-rgb), 0.36)',
                background: 'rgba(var(--err-rgb), 0.07)',
                color: 'var(--err-soft)',
              }}
            >
              This action never settled, so whether it applied is unknown. Verify the target's
              current state before retrying — repeating an override that already landed is its
              own incident.
            </p>
          )}

          {data.errorMessage && (
            <div>
              <FieldLabel>Error</FieldLabel>
              <pre className="border-bd bg-bg-alt text-tx2 mt-0.5 overflow-x-auto rounded-lg border p-2 font-mono text-[11px] whitespace-pre-wrap">
                {data.errorMessage}
              </pre>
            </div>
          )}

          {/* What the operator was warned about, not just what happened. */}
          {data.consequences.length > 0 && (
            <div>
              <FieldLabel>Warned about at confirm time</FieldLabel>
              <ul className="mt-1 flex flex-col gap-1">
                {data.consequences.map((consequence, index) => (
                  <li
                    key={index}
                    className="text-[12.5px]"
                    style={{
                      color:
                        consequence.severity === 'critical'
                          ? 'var(--err-soft)'
                          : consequence.severity === 'warning'
                            ? 'var(--warn)'
                            : 'var(--tx2)',
                    }}
                  >
                    {consequence.text}
                    {consequence.count !== null && (
                      <span className="mesh-mono text-tx3 ml-1.5 text-[11px]">
                        ×{consequence.count}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.diff.length > 0 && (
            <div>
              <FieldLabel>Changed</FieldLabel>
              <div className="mt-1 overflow-x-auto">
                <table className="w-full min-w-max border-collapse text-left">
                  <thead>
                    <tr className="border-bd border-b">
                      {['Field', 'Before', 'After'].map((heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="font-head text-tx3 py-1 pr-4 text-[10px] font-semibold uppercase"
                          style={{ letterSpacing: '0.14em' }}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.diff.map((entry) => (
                      <tr key={entry.field} className="border-bd/60 border-b last:border-b-0">
                        <td className="mesh-mono text-tx2 py-1.5 pr-4 text-[11.5px]">
                          {entry.field}
                        </td>
                        <td className="mesh-mono text-tx3 py-1.5 pr-4 text-[11.5px]">
                          {formatValue(entry.before)}
                        </td>
                        <td className="mesh-mono text-tx py-1.5 pr-4 text-[11.5px]">
                          {formatValue(entry.after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
            <MetaField label="Operator" value={`${data.actor.name} · ${data.actor.role}`} />
            <MetaField label="Executed via" value={data.executedVia} />
            <MetaField label="Environment" value={data.meshEnv} />
            <MetaField
              label="Started"
              value={data.startedAt?.replace('T', ' ').slice(0, 19) ?? '—'}
            />
            {data.delegateEndpoint && (
              <MetaField label="Delegate endpoint" value={data.delegateEndpoint} />
            )}
            {data.enqueuedJobs.length > 0 && (
              <MetaField label="Enqueued jobs" value={data.enqueuedJobs.join(', ')} />
            )}
            {data.impersonatedUserId && (
              <MetaField label="While impersonating" value={data.impersonatedUserId} />
            )}
            <MetaField label="Idempotency key" value={data.idempotencyKey} />
          </dl>
        </div>
      )}
    </section>
  );
}

/** Renders a Mixed diff value without assuming a shape. */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '(empty)';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="font-head text-tx3 text-[10px] font-semibold uppercase"
      style={{ letterSpacing: '0.14em' }}
    >
      {children}
    </span>
  );
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <FieldLabel>{label}</FieldLabel>
      <dd className="mesh-mono text-tx2 mt-0.5 truncate text-[11.5px]" title={value}>
        {value}
      </dd>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="border-bd bg-bg-alt text-tx2 h-9 rounded-lg border px-2 text-[12.5px] outline-none"
      >
        <option value="">{label}: any</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="mesh-mono text-tx3 text-[11.5px]">
        {((page - 1) * pageSize + 1).toLocaleString()}–
        {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Prev
        </button>
        <span className="mesh-mono text-tx3 text-[11.5px]">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
